import { useCallback, useEffect, useRef } from 'react';
import firebase from 'firebase/app';
import UserContext from '../context/user';
import useHandleError from './useHandleError';
import { useContext } from 'react';

type PreparedVerifier = { verifier: firebase.auth.RecaptchaVerifier; container: HTMLDivElement };

export default function useVerifyPhoneNumber() {
  const { saveUser } = useContext(UserContext);

  const handleError = useHandleError();

  // reCAPTCHA tokens are single-use, so every SMS needs its own verifier — the
  // old shared app-level one only worked for the first send. Rendering one is
  // a network handshake, so keep a rendered spare ready and replace it after
  // each send instead of paying that cost while the user waits.
  const spare = useRef<PreparedVerifier | null>(null);

  const makeVerifier = useCallback((): PreparedVerifier => {
    const container = document.createElement('div');
    container.style.display = 'none';
    document.body.appendChild(container);
    const verifier = new firebase.auth.RecaptchaVerifier(container, { size: 'invisible' });
    // Start the handshake now; failures surface on the send itself.
    verifier.render().catch(() => undefined);
    return { verifier, container };
  }, []);

  const disposeVerifier = useCallback((prepared: PreparedVerifier) => {
    try {
      prepared.verifier.clear();
    } catch (e) {
      // already cleared
    }
    prepared.container.remove();
  }, []);

  // Warm a verifier ahead of the first send (call when the form opens).
  const prepareVerifier = useCallback(() => {
    if (!spare.current) spare.current = makeVerifier();
  }, [makeVerifier]);

  useEffect(() => {
    return () => {
      if (spare.current) {
        disposeVerifier(spare.current);
        spare.current = null;
      }
    };
  }, [disposeVerifier]);

  const verifyPhoneNumber = useCallback(
    async (phoneNumber: string, email: string | undefined) => {
      const active = spare.current || makeVerifier();
      spare.current = null;
      try {
        var provider = new firebase.auth.PhoneAuthProvider();
        const verificationId = await provider.verifyPhoneNumber(phoneNumber, active.verifier);
        saveUser({
          firebase: { verificationId: verificationId },
          phoneNumber: phoneNumber,
          email: email,
        });
      } catch (error) {
        handleError(error);
        throw error;
      } finally {
        disposeVerifier(active);
        spare.current = makeVerifier(); // ready for a resend
      }
    },
    [disposeVerifier, handleError, makeVerifier, saveUser]
  );
  return { verifyPhoneNumber, prepareVerifier };
}
